/**
 * .gitignore Generator Tool
 *
 * Select languages/frameworks to assemble a .gitignore file.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { IGeneratorTool } from '@/interfaces/ITool';

const TEMPLATES: Record<string, string> = {
  node: `# Node
node_modules/
npm-debug.log*
yarn-debug.log*
yarn-error.log*
.pnpm-debug.log*
dist/
build/
.env
.env.local
.env.*.local
coverage/
.cache/
.parcel-cache/`,

  python: `# Python
__pycache__/
*.py[cod]
*$py.class
*.egg-info/
dist/
build/
*.egg
.env
venv/
.venv/
*.whl
.pytest_cache/
.mypy_cache/
.coverage
htmlcov/`,

  java: `# Java
*.class
*.jar
*.war
*.ear
target/
.gradle/
build/
out/
.settings/
.classpath
.project`,

  go: `# Go
bin/
pkg/
*.exe
*.test
*.out
vendor/`,

  rust: `# Rust
target/
Cargo.lock
**/*.rs.bk`,

  dotnet: `# .NET
bin/
obj/
*.user
*.suo
*.cache
*.dll
*.exe
*.pdb
packages/
.vs/
*.nupkg`,

  react: `# React
node_modules/
build/
dist/
.env
.env.local
.env.*.local
coverage/`,

  angular: `# Angular
node_modules/
dist/
tmp/
out-tsc/
.angular/
.env
coverage/
e2e/`,

  vue: `# Vue
node_modules/
dist/
.env.local
.env.*.local
coverage/
*.local`,

  ios: `# iOS
build/
DerivedData/
*.pbxuser
*.mode1v3
*.mode2v3
*.perspectivev3
*.xccheckout
*.xcscmblueprint
*.xcworkspace
!default.xcworkspace
xcuserdata/
*.moved-aside
Pods/
Carthage/Build/`,

  android: `# Android
*.apk
*.ap_
*.dex
*.class
bin/
gen/
out/
build/
.gradle/
local.properties
*.iml
.idea/`,

  unity: `# Unity
[Ll]ibrary/
[Tt]emp/
[Oo]bj/
[Bb]uild/
[Bb]uilds/
[Ll]ogs/
[Uu]ser[Ss]ettings/
*.csproj
*.unityproj
*.sln
*.suo
*.tmp
*.user
*.pidb
*.booproj
*.svd
*.pdb
*.mdb
*.opendb
*.VC.db
sysinfo.txt
crashlytics-build.properties`,
};

const IDE_TEMPLATE = `# IDE
.vscode/
.idea/
.vs/
*.swp
*.swo
*~
.project
.classpath
.settings/`;

const OS_TEMPLATE = `# OS
.DS_Store
.DS_Store?
._*
.Spotlight-V100
.Trashes
Thumbs.db
ehthumbs.db
Desktop.ini
$RECYCLE.BIN/`;

const TEMPLATE_KEYS = Object.keys(TEMPLATES);

export const GitignoreGeneratorTool: IGeneratorTool = {
  id: 'gitignore-generator',
  name: '.gitignore Generator',
  description: 'Generate .gitignore files for popular languages and frameworks',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['gitignore', 'git', 'ignore', 'generator', 'template'],

  seo: {
    slug: 'gitignore-generator',
    pageTitle: '.gitignore Generator - Free Online Tool | SWE DevTools',
    metaDescription: 'Generate .gitignore files for Node, Python, Java, Go, Rust, .NET, React, Angular, Vue, iOS, Android, and Unity. Includes IDE and OS patterns.',
    keywords: ['gitignore generator', 'gitignore template', 'generate gitignore', 'git ignore file', 'gitignore builder'],
    relatedTools: ['robots-txt-generator', 'meta-tag-generator', 'slugify-generator'],
    schemaType: 'WebApplication',
  },

  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    ...TEMPLATE_KEYS.map(key => ({
      name: key,
      type: 'boolean' as const,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      defaultValue: false,
      description: `Include ${key} patterns`,
    })),
    {
      name: 'includeIde',
      type: 'boolean' as const,
      label: 'IDE Files',
      defaultValue: true,
      description: 'Include IDE patterns (.vscode, .idea, .vs)',
    },
    {
      name: 'includeOs',
      type: 'boolean' as const,
      label: 'OS Files',
      defaultValue: true,
      description: 'Include OS patterns (.DS_Store, Thumbs.db)',
    },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const sections: string[] = [];

    // Add selected language/framework templates
    for (const key of TEMPLATE_KEYS) {
      if (config[key] === true) {
        sections.push(TEMPLATES[key]);
      }
    }

    // Add IDE patterns
    if (config.includeIde !== false) {
      sections.push(IDE_TEMPLATE);
    }

    // Add OS patterns
    if (config.includeOs !== false) {
      sections.push(OS_TEMPLATE);
    }

    if (sections.length === 0) {
      return '# Select languages/frameworks to generate .gitignore patterns';
    }

    return sections.join('\n\n');
  },
};
